import React from 'react';
import { HalfPieChartProps } from './HalfPieChart.props';
import { Box } from '@mui/material';
import { PieChart, pieArcLabelClasses, pieArcClasses } from '@mui/x-charts/PieChart';
import './HalfPieChart.css';
import { roundPhishFitScore } from '../../utils/util';

const NO_SCORE_INFO = { level: 'No Score', color: '#9DAAAA', textcolor: '#000000E5' };

// Helper function to get resilience level and color based on score
const getResilienceLevel = (
  score: number | string | null | undefined
): { level: string; color: string; textcolor: string } => {
  if (score === null || score === undefined) {
    return NO_SCORE_INFO;
  }

  const numericScore = typeof score === 'string' ? Number(score) : score;
  if (isNaN(numericScore)) {
    return NO_SCORE_INFO;
  }

  if (numericScore >= 85 && numericScore <= 100) {
    return { level: 'Elite', color: '#A2BC30', textcolor: '#000000E5' };
  } else if (numericScore >= 70 && numericScore <= 84) {
    return { level: 'Strong', color: '#81D4FB', textcolor: '#000000E5' };
  } else if (numericScore >= 40 && numericScore <= 69) {
    return { level: 'Emerging', color: '#FFC107', textcolor: '#000000E5' };
  } else if (numericScore >= 0 && numericScore <= 39) {
    return { level: 'Developing', color: '#DB071E', textcolor: '#ffffff' };
  } else {
    return NO_SCORE_INFO;
  }
};

const HalfPieChart: React.FC<HalfPieChartProps> = ({ valueScore }) => {
  const pieChartStartPoint = -90;
  const numericScore =
    valueScore !== null && valueScore !== undefined && valueScore !== '---'
      ? Number(valueScore)
      : NaN;
  const hasScore = !isNaN(numericScore);
  const roundedScore = hasScore ? roundPhishFitScore(numericScore) : 0;
  const pieChartEndPoint = hasScore
    ? pieChartStartPoint + 1.8 * roundedScore
    : pieChartStartPoint;
  const resilienceInfo = hasScore ? getResilienceLevel(roundedScore) : NO_SCORE_INFO;

  return (
    <>
      <Box className={'left-partial-threat-score'}>
        <Box className={'chart'}>
          <PieChart
            series={[
              {
                startAngle: pieChartStartPoint,
                endAngle: 90,
                paddingAngle: 0,
                innerRadius: 100,
                outerRadius: 145,
                data: [{ value: 100, color: '#F2F3F3' }]
              },
              {
                startAngle: pieChartStartPoint,
                endAngle: pieChartEndPoint,
                innerRadius: 104,
                outerRadius: 141,
                arcLabel: hasScore ? 'value' : () => '---',
                data: [
                  {
                    label: 'item',
                    value: roundedScore,
                    color: resilienceInfo.level === 'Strong' ? '#03A9F5' : resilienceInfo.color
                  }
                ]
              }
            ]}
            sx={{
              [`& .${pieArcLabelClasses.root}`]: {
                fill: '#000000E5',
                textShadow: '0 0 2px rgba(0, 0, 0, 0.25)',
                fontFamily: 'Montserrat',
                fontSize: '45px',
                fontWeight: 700,
                transform: 'translate(0, -15%)!important'
              },
              [`& .${pieArcClasses.root}`]: {
                stroke: '#E0E0E0'
              }
            }}
            skipAnimation
            tooltip={{ trigger: 'none' }}
            slotProps={{
              legend: { hidden: true }
            }}
            margin={{ right: 0, left: 0, top: 145 }}
          />
        </Box>
        <Box
          className={'chart-info'}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '-10px'
          }}>
          <span
            style={{
              width: '42px',
              color: '#808888',
              fontSize: '18px'
            }}>
            0
          </span>
          <span
            style={{
              color: '#808888',
              fontSize: '16px'
            }}>
            Resilience Score
          </span>
          <span
            style={{
              width: '42px',
              color: '#808888',
              fontSize: '18px',
              textAlign: 'right',
              paddingRight: '6px'
            }}>
            100
          </span>
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: '20px'
          }}>
          <span
            style={{
              backgroundColor: resilienceInfo.color,
              color: resilienceInfo.textcolor,
              borderRadius: '24px',
              padding: '10px 16px',
              fontWeight: 'bold',
              fontSize: '16px',
              textTransform: 'uppercase',
              placeItems: 'center'
            }}>
            {resilienceInfo.level}
          </span>
        </Box>
      </Box>
    </>
  );
};

export default HalfPieChart;
